import { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import GlobalSearch from "@/components/GlobalSearch";
import AiAssistant from "@/components/AiAssistant";
import { isAdmin as isAdminRole, isSuperAdmin, isStaffOrAbove, hasRetrofitAccess, ROLE_BADGE_STYLES } from "@/utils/roles";
import { T4RWordmark } from "@/components/t4r/T4RWordmark";
import { BrandLogo } from "@/components/BrandLogo";
import { useBranding } from "@/contexts/BrandingContext";
import { WorkspaceSwitch, WORKSPACE_KEY } from "@/components/t4r/WorkspaceSwitch";
import { MfaEnforcementBanner } from "@/components/MfaEnforcementBanner";
import {
  LayoutDashboard, Users, GraduationCap, Award, CalendarDays,
  BarChart3, Mail, LogOut, Menu, X, ChevronLeft, Receipt, Tags, Settings as SettingsIcon, Shield, PoundSterling, KeyRound, BedDouble, PhoneCall, Building2, BookOpen, UserCog, FileArchive, ShieldCheck, Plane, ClipboardCheck,
  Landmark, FileSignature,
} from "lucide-react";

// Nav visibility per role. Everything without a `min` is visible to all
// authenticated users. `min` = minimum role tier required:
//   "staff"      -> staff / admin / superadmin
//   "admin"      -> admin / superadmin   (was previously "adminOnly")
//   "superadmin" -> superadmin only
const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/courses", label: "Courses", icon: CalendarDays },
  { to: "/qualifications", label: "Qualifications", icon: GraduationCap },
  { to: "/enquiries", label: "Enquiries", icon: PhoneCall },
  { to: "/members", label: "Members", icon: Users },
  { to: "/businesses", label: "Businesses", icon: Building2 },
  { to: "/certificates", label: "Certificates", icon: Award },
  { to: "/teach", label: "Teach", icon: BookOpen, min: "staff" },
  { to: "/training-matrix", label: "Training Matrix", icon: ShieldCheck, min: "admin" },
  { to: "/staff-calendar", label: "Staff Calendar", icon: BedDouble, min: "staff" },
  { to: "/holidays", label: "Holidays", icon: Plane, min: "staff" },
  { to: "/pricing", label: "Pricing", icon: Tags, min: "admin" },
  { to: "/payments", label: "Payments", icon: PoundSterling, min: "admin" },
  { to: "/expenses", label: "Expenses", icon: PoundSterling, min: "admin" },
  { to: "/imports", label: "Bulk Imports", icon: FileArchive, min: "superadmin" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, min: "admin" },
  { to: "/email-logs", label: "Email Logs", icon: Mail, min: "superadmin" },
  { to: "/agreements", label: "Agreements", icon: FileSignature, min: "superadmin" },
  { to: "/governance", label: "Governance", icon: Landmark, min: "provider" },
];

const t4rNavItems = (short) => [
  { to: "/t4r", label: `${short} Dashboard`, icon: LayoutDashboard, testId: "nav-retrofit-dashboard" },
  { to: "/t4r/courses", label: `${short} Courses`, icon: BookOpen, testId: "nav-retrofit-courses" },
  { to: "/t4r/learners", label: `${short} Learners`, icon: Users, testId: "nav-retrofit-learners" },
  { to: "/t4r/attempts", label: "Assessment & Marking", icon: ClipboardCheck, testId: "nav-quiz-attempts" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const canRetrofit = hasRetrofitAccess(user);
  const isT4R = location.pathname.startsWith("/t4r");
  const { lms_short_name, lms_name, brand_color } = useBranding();
  const items = isT4R ? t4rNavItems(lms_short_name || "LMS") : navItems;

  useEffect(() => {
    if (location.pathname === "/" && canRetrofit && localStorage.getItem(WORKSPACE_KEY) === "t4r") navigate("/t4r", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { localStorage.setItem(WORKSPACE_KEY, isT4R ? "t4r" : "nts"); }, [isT4R]);

  const pageTitle = items.find(
    (n) => n.to === location.pathname || (n.to !== "/" && n.to !== "/t4r" && location.pathname.startsWith(n.to))
  )?.label || ({ "/settings": "Admin Dashboard", "/admin": "Admin Dashboard", "/profile": "My Profile" }[location.pathname] || "Dashboard");

  const passesMin = (min) => {
    if (!min) return true;
    if (min === "provider") return !!user?.is_provider;
    if (min === "superadmin") return isSuperAdmin(user?.role);
    if (min === "admin") return isAdminRole(user?.role);
    if (min === "staff") return isStaffOrAbove(user?.role);
    return true;
  };

  const visibleNavItems = items.filter((n) => passesMin(n.min));

  const roleBadgeColor = ROLE_BADGE_STYLES;

  return (
    <div className="min-h-screen flex bg-background" data-testid="app-layout" data-workspace={isT4R ? "t4r" : "nts"}>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-50 h-screen border-r flex flex-col transition-all duration-200 ${isT4R ? "bg-[#0B2418] border-[#123524] text-white" : "bg-white border-border"} ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        } ${collapsed ? "w-[68px]" : "w-[240px]"}`}
        data-testid="sidebar"
      >
        <div className={`h-16 flex items-center border-b px-3 ${isT4R ? "border-white/10" : "border-border"} ${collapsed ? "justify-center" : "justify-between"}`}>
          {isT4R && <T4RWordmark compact={collapsed} dark />}
          {!isT4R && <BrandLogo size={collapsed ? "sm" : "lg"} compact={collapsed} />}
          <button
            onClick={() => { if (window.innerWidth < 1024) setSidebarOpen(false); else setCollapsed(!collapsed); }}
            className={`p-1 rounded transition-colors ${isT4R ? "hover:bg-white/10 text-green-100" : "hover:bg-secondary"}`}
            data-testid="sidebar-toggle"
          >
            {window.innerWidth < 1024 ? <X size={18} /> : <ChevronLeft size={18} className={collapsed ? "rotate-180" : ""} />}
          </button>
        </div>
        {canRetrofit && (
          <div className={`px-3 pt-3 ${collapsed ? "flex justify-center" : ""}`}>
            <WorkspaceSwitch isT4R={isT4R} collapsed={collapsed} />
          </div>
        )}
        <nav className="flex-1 py-2 px-2 space-y-0.5 overflow-y-auto">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/" || item.to === "/t4r"}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `nav-link flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${
                  isT4R
                    ? (isActive ? "bg-green-500 text-[#0B2418]" : "text-green-100/80 hover:bg-white/10 hover:text-white")
                    : (isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900")
                } ${collapsed ? "justify-center px-2" : ""}`
              }
              style={({ isActive }) => (!isT4R && isActive && brand_color ? { backgroundColor: brand_color } : undefined)}
              data-testid={item.testId || `nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <item.icon size={18} />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>
        <div className={`p-3 border-t relative ${isT4R ? "border-white/10" : "border-border"} ${collapsed ? "text-center" : ""}`}>
          {profileMenuOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setProfileMenuOpen(false)} />
              <div className="absolute bottom-full left-3 right-3 mb-2 bg-white border border-border rounded-lg shadow-lg z-40 overflow-hidden" data-testid="profile-mini-menu">
                <div className="px-3 py-2 border-b border-border bg-slate-50">
                  <p className="text-xs font-semibold text-foreground truncate">{user?.name}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{user?.email}</p>
                </div>
                {isAdminRole(user?.role) && (
                  <Link
                    to="/admin"
                    onClick={() => setProfileMenuOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-100 text-slate-700"
                    data-testid="admin-dashboard-link"
                  >
                    <Shield size={15} className="text-sky-600" />
                    <span>Admin Dashboard</span>
                  </Link>
                )}
                <Link
                  to="/profile"
                  onClick={() => setProfileMenuOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-100 text-slate-700"
                  data-testid="profile-edit-link"
                >
                  <UserCog size={15} className="text-slate-600" />
                  <span>My profile</span>
                </Link>
                <Link
                  to="/change-password"
                  onClick={() => setProfileMenuOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-100 text-slate-700"
                  data-testid="change-password-link"
                >
                  <KeyRound size={15} className="text-slate-600" />
                  <span>Change Password</span>
                </Link>
                <button
                  onClick={() => { setProfileMenuOpen(false); logout(); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-red-50 text-red-600 text-left"
                  data-testid="profile-logout-btn"
                >
                  <LogOut size={15} />
                  <span>Sign Out</span>
                </button>
              </div>
            </>
          )}
          <button
            onClick={() => setProfileMenuOpen(!profileMenuOpen)}
            className={`w-full flex items-center gap-2 p-1 rounded transition-colors ${isT4R ? "hover:bg-white/10" : "hover:bg-slate-100"} ${collapsed ? "justify-center" : ""}`}
            data-testid="profile-menu-btn"
          >
            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0 text-left">
                <p className={`text-xs font-medium truncate ${isT4R ? "text-white" : "text-foreground"}`}>{user?.name}</p>
                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${roleBadgeColor[user?.role] || ""}`}>
                  {user?.role}
                </Badge>
              </div>
            )}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className={`sticky top-0 z-30 h-14 bg-white border-b flex ${isT4R ? "border-b-2 border-b-green-500" : "border-border"} items-center justify-between px-4 lg:px-6`} data-testid="top-header">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-1.5 rounded hover:bg-secondary"
              data-testid="mobile-menu-btn"
            >
              <Menu size={20} />
            </button>
            <h1 className="font-heading font-bold text-lg tracking-tight text-foreground hidden md:block">{pageTitle}</h1>
            {isT4R && <span className="hidden sm:inline-flex items-center rounded-full bg-green-50 px-2.5 py-0.5 text-[11px] font-semibold text-green-700 border border-green-200" data-testid="t4r-workspace-badge">{lms_name}</span>}
          </div>
          <div className="flex items-center gap-3 flex-1 max-w-lg mx-4">
            {!isT4R && <GlobalSearch />}
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="hidden sm:inline">{user?.email}</span>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <MfaEnforcementBanner />
          <Outlet />
        </main>
      </div>
      {user?.role !== "student" && !isT4R && <AiAssistant />}
    </div>
  );
}
